import type { EstadiaCliente, Equipamento, ProcessoMovimentacao, StatusEstadiaCliente } from '../../models';

/** Filtro do painel de resumo da aba "Estadia em Cliente" — mesma convenção do painel de
 * Solicitações (solicitacoesResumo.utils): lista vazia = sem filtro naquele critério. */
export interface SelecaoEstadias {
  status: StatusEstadiaCliente[];
  clientes: string[];
  motivos: ProcessoMovimentacao[];
}

export type FacetaEstadias = keyof SelecaoEstadias;

export interface ContagensEstadias {
  status: Map<StatusEstadiaCliente, number>;
  clientes: Map<string, number>;
  motivos: Map<ProcessoMovimentacao, number>;
}

/** Padrão: só as ativas — encerradas ficam escondidas até clicar no card "Encerrada"
 * (decisão do usuário, mesmo raciocínio do padrão "Novo/Em Andamento" das Solicitações). */
export const STATUS_ESTADIA_PADRAO: StatusEstadiaCliente[] = ['em_transito', 'em_homologacao', 'pronta_aguardando_destino'];

export interface ContextoBuscaEstadia {
  equipamentoPorId: Map<string, Equipamento>;
  nomeClientePorId: Map<string, string>;
  nomeColaboradorPorId: Map<string, string>;
}

/** Busca livre — substring, sem distinguir maiúsculas, em patrimônio/modelo/nº série do
 * Equipamento, nome do Cliente e do colaborador cotado. */
export function estadiaCorrespondeTexto(estadia: EstadiaCliente, termo: string, contexto: ContextoBuscaEstadia): boolean {
  const alvo = termo.trim().toLowerCase();
  if (!alvo) return true;
  const equipamento = contexto.equipamentoPorId.get(estadia.equipamentoId);
  return [
    equipamento?.patrimonio,
    equipamento?.modelo,
    equipamento?.nSerie,
    contexto.nomeClientePorId.get(estadia.clienteId),
    estadia.colaboradorCotadoId ? contexto.nomeColaboradorPorId.get(estadia.colaboradorCotadoId) : null,
  ].some((valor) => valor?.toLowerCase().includes(alvo));
}

export function aplicarSelecaoEstadias(
  lista: EstadiaCliente[],
  selecao: SelecaoEstadias,
  ignorar?: FacetaEstadias
): EstadiaCliente[] {
  return lista.filter(
    (estadia) =>
      (ignorar === 'status' || selecao.status.length === 0 || selecao.status.includes(estadia.status)) &&
      (ignorar === 'clientes' || selecao.clientes.length === 0 || selecao.clientes.includes(estadia.clienteId)) &&
      (ignorar === 'motivos' || selecao.motivos.length === 0 || selecao.motivos.includes(estadia.motivo))
  );
}

function contarPor<K>(lista: EstadiaCliente[], chave: (estadia: EstadiaCliente) => K): Map<K, number> {
  const contagem = new Map<K, number>();
  for (const estadia of lista) contagem.set(chave(estadia), (contagem.get(chave(estadia)) ?? 0) + 1);
  return contagem;
}

/** Cada critério conta com os OUTROS filtros aplicados, menos ele mesmo — assim uma opção
 * desmarcada mostra quantas viriam ao marcá-la (mesma regra de contarResumo). `base` já
 * vem filtrada pela busca livre. */
export function contarEstadias(base: EstadiaCliente[], selecao: SelecaoEstadias): ContagensEstadias {
  return {
    status: contarPor(aplicarSelecaoEstadias(base, selecao, 'status'), (e) => e.status),
    clientes: contarPor(aplicarSelecaoEstadias(base, selecao, 'clientes'), (e) => e.clienteId),
    motivos: contarPor(aplicarSelecaoEstadias(base, selecao, 'motivos'), (e) => e.motivo),
  };
}
